import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import Cookies from "js-cookie";

interface Organization {
    id: string;
    name: string;
    logoUrl: string | null;
}

interface Role {
    id: string;
    type: string;
    organization: Organization;
}

interface User {
    id: string;
    email: string;
    name: string;
    roles: Role[];
    role: string;
    defaultOrganization?: Organization;
    defaultOrganizationId: string;
}

interface SessionContextProps {
    isLoggedIn: boolean;
    user: User | null;
    currentOrganization: Organization | null;
    login: (token: string, user: User) => void;
    logout: () => void;
}

const SessionContext = createContext<SessionContextProps | undefined>(undefined);

export const SessionProvider = ({ children }: { children: ReactNode }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);

    useEffect(() => {
        const token = Cookies.get("token");
        const userData = Cookies.get("user");
        if (token && userData) {
            const parsedUser = JSON.parse(userData);
            setIsLoggedIn(true);
            setUser(parsedUser);
            setCurrentOrganization(parsedUser.defaultOrganization || null);
        }
    }, []);

    const login = (token: string, user: User) => {
        Cookies.set("token", token, { expires: 7, secure: true, sameSite: "strict" });
        Cookies.set("user", JSON.stringify(user), { expires: 7, secure: true, sameSite: "strict" });
        setIsLoggedIn(true);
        setUser(user);
        setCurrentOrganization(user.defaultOrganization || null);
    };

    const logout = () => {
        Cookies.remove("token");
        Cookies.remove("user");
        setIsLoggedIn(false);
        setUser(null);
        setCurrentOrganization(null);
    };

    return (
        <SessionContext.Provider value={{
            isLoggedIn,
            user,
            currentOrganization,
            login,
            logout
        }}>
            {children}
        </SessionContext.Provider>
    );
};

export const useSession = () => {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error("useSession must be used within a SessionProvider");
    }
    return context;
};